import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

type Message = {
  id: string;
  role: 'user' | 'bot';
  text: string;
  statusText?: string;
};

function generateSessionId(): string {
  return crypto.randomUUID();
}

function renderMessageContent(text: string, accent: string) {
  // Split on [IMAGE:url] markers — may appear anywhere in the text
  const parts = text.split(/\[IMAGE:([^\]]+)\]/);
  return parts.map((part, i) => {
    if (i % 2 === 1) {
      // Odd indexes are captured URL groups
      return (
        <img
          key={i}
          src={part.trim()}
          alt="Knowledge base image"
          className="rounded-lg max-w-full my-2"
          style={{ maxHeight: '260px', border: `1px solid ${accent}40` }}
        />
      );
    }
    return part ? <span key={i} style={{ whiteSpace: 'pre-wrap' }}>{part}</span> : null;
  });
}

export const GeminiKBChatPage = () => {
  const { shortCode } = useParams<{ shortCode: string }>();
  const [messages, setMessages]   = useState<Message[]>([]);
  const [input, setInput]         = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError]         = useState<string | null>(null);
  // Fresh session every page load
  const [sessionId] = useState<string>(() => generateSessionId());
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef  = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const sendMessage = async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text || isLoading) return;

    setError(null);
    const userMessage: Message = { id: crypto.randomUUID(), role: 'user', text };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    // Optimistically append an empty bot message we'll stream into
    const botId = crypto.randomUUID();
    setMessages(prev => [...prev, { id: botId, role: 'bot', text: '' }]);

    try {
      const res = await fetch('/api/gemini-kb-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, sessionId, shortCode }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Unknown error' })) as { error?: string };
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }

      if (!res.body) throw new Error('No response body');

      const reader  = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const events = buffer.split('\n\n');
        buffer = events.pop() ?? '';

        for (const block of events) {
          const eventMatch = block.match(/^event: (\w+)/);
          const dataMatch  = block.match(/^data: (.+)$/m);
          if (!eventMatch || !dataMatch) continue;

          const eventType = eventMatch[1];
          let payload: Record<string, string>;
          try { payload = JSON.parse(dataMatch[1]); } catch { continue; }

          if (eventType === 'status') {
            setMessages(prev =>
              prev.map(m =>
                m.id === botId ? { ...m, statusText: payload.text ?? '' } : m,
              ),
            );
          } else if (eventType === 'chunk') {
            setMessages(prev =>
              prev.map(m =>
                m.id === botId ? { ...m, statusText: undefined, text: m.text + (payload.text ?? '') } : m,
              ),
            );
          } else if (eventType === 'error') {
            setMessages(prev =>
              prev.map(m =>
                m.id === botId
                  ? { ...m, text: payload.error ?? 'Something went wrong.' }
                  : m,
              ),
            );
          }
          // 'done' event — nothing extra needed
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Connection error. Please try again.';
      setError(msg);
      // Remove the empty bot message
      setMessages(prev => prev.filter(m => m.id !== botId || m.text));
    } finally {
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const resetSession = () => window.location.reload();

  const showEmpty = messages.length === 0 && !isLoading;

  // ── Accent color for this page (slate-indigo, distinct from gold bartender) ──
  const accent = '#6366f1'; // indigo-500

  return (
    <div
      className="h-screen flex flex-col"
      style={{ background: '#0d0d0d', color: '#e2e2e8', fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-4 shrink-0"
        style={{ borderBottom: '1px solid #1e1e2a' }}
      >
        <div>
          <span
            className="text-xs tracking-[0.2em] uppercase font-semibold"
            style={{ color: accent }}
          >
            Knowledge Base
          </span>
          <p
            className="text-[10px] tracking-[0.15em] uppercase mt-0.5"
            style={{ color: '#444' }}
          >
            {shortCode}
          </p>
        </div>
        <button
          onClick={resetSession}
          title="New conversation"
          className="transition-colors p-1.5 rounded"
          style={{ color: '#444' }}
          onMouseEnter={e => (e.currentTarget.style.color = accent)}
          onMouseLeave={e => (e.currentTarget.style.color = '#444')}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
          </svg>
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-5 space-y-3">
        {showEmpty && (
          <div className="flex flex-col items-center gap-3 mt-16">
            <p className="text-xs tracking-[0.2em] uppercase" style={{ color: '#444' }}>
              Ask anything about the documents
            </p>
          </div>
        )}

        {messages.map(msg => (
          <div
            key={msg.id}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            style={{ animation: 'msgIn 0.18s ease-out both' }}
          >
            <div
              className="max-w-[78%] px-4 py-2.5 rounded-2xl text-sm break-words leading-relaxed"
              style={
                msg.role === 'user'
                  ? {
                      background: accent,
                      color: '#fff',
                      borderBottomRightRadius: '4px',
                      fontWeight: 500,
                    }
                  : {
                      background: '#161620',
                      color: '#d4d4dc',
                      border: '1px solid #252530',
                      borderBottomLeftRadius: '4px',
                    }
              }
            >
              {msg.text
                ? (msg.role === 'bot' ? renderMessageContent(msg.text, accent) : msg.text)
                : msg.role === 'bot' && msg.statusText
                  ? (
                    <span className="flex items-center gap-2" style={{ opacity: 0.6, fontSize: '0.75rem', fontStyle: 'italic' }}>
                      <span className="inline-flex gap-1">
                        <span className="w-1 h-1 rounded-full animate-bounce" style={{ background: 'currentColor', animationDelay: '0ms' }} />
                        <span className="w-1 h-1 rounded-full animate-bounce" style={{ background: 'currentColor', animationDelay: '120ms' }} />
                        <span className="w-1 h-1 rounded-full animate-bounce" style={{ background: 'currentColor', animationDelay: '240ms' }} />
                      </span>
                      {msg.statusText}
                    </span>
                  )
                  : <span style={{ opacity: 0.4 }}>…</span>
              }
            </div>
          </div>
        ))}

        {isLoading && messages[messages.length - 1]?.role !== 'bot' && (
          // Loading dots before bot message appears
          <div className="flex justify-start" style={{ animation: 'msgIn 0.18s ease-out both' }}>
            <div
              className="px-4 py-3 rounded-2xl"
              style={{
                background: '#161620',
                border: '1px solid #252530',
                borderBottomLeftRadius: '4px',
              }}
            >
              <div className="flex gap-1.5 items-center">
                <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: accent, animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: accent, animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: accent, animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        {error && (
          <div
            className="mx-auto max-w-sm text-center text-sm rounded-lg px-4 py-3"
            style={{ color: '#f87171', background: '#1a0a0a', border: '1px solid #3a1a1a' }}
          >
            {error}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div
        className="px-4 py-3 flex gap-2 items-center shrink-0"
        style={{ borderTop: '1px solid #1a1a20', background: '#0d0d0d' }}
      >
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask a question…"
          disabled={isLoading}
          className="flex-1 text-sm px-4 py-2.5 rounded-full outline-none disabled:opacity-40 transition-all"
          style={{ background: '#161620', border: '1px solid #252530', color: '#e2e2e8' }}
          onFocus={e => (e.currentTarget.style.borderColor = accent)}
          onBlur={e => (e.currentTarget.style.borderColor = '#252530')}
        />
        <button
          onClick={() => sendMessage()}
          disabled={isLoading || !input.trim()}
          title="Send"
          className="w-10 h-10 flex items-center justify-center rounded-full shrink-0 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          style={{ background: accent, color: '#fff' }}
          onMouseEnter={e => { if (!(e.currentTarget as HTMLButtonElement).disabled) (e.currentTarget as HTMLButtonElement).style.background = '#818cf8'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = accent; }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>

      <style>{`
        @keyframes msgIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        input::placeholder { color: #333340; }
      `}</style>
    </div>
  );
};
